import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { AutofillMonitor } from '@angular/cdk/text-field';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALIDATORS, NgControl, UntypedFormBuilder } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldControl } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { validateAddress } from '@libraries/helpers';
import { Observable, of, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  publishReplay,
  refCount,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { GoogleMaps } from '../google-maps.service';

type AutocompletePrediction = google.maps.places.AutocompletePrediction;

interface UserInputtedAddress {
  address: string;
  label?: string;
}

type GooglePlaceAddress = google.maps.places.PlaceResult & {
  label: string;
  address?: string;
};

type AddressObject = UserInputtedAddress | GooglePlaceAddress;

type AutocompleteResult = AutocompletePrediction & {
  label: string;
  value: string;
};

@Component({
  providers: [
    {
      provide: NG_VALIDATORS,
      useValue: validateAddress,
      multi: true,
    },
    {
      provide: MatFormFieldControl,
      useExisting: AddressAutocompleteComponent,
    },
  ],
  selector: 'ui-address-autocomplete',
  exportAs: 'ui-address-autocomplete',
  templateUrl: './address-autocomplete.component.html',
  styleUrls: ['./address-autocomplete.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddressAutocompleteComponent
  implements OnInit, OnDestroy, ControlValueAccessor, MatFormFieldControl<string | void>
{
  static nextId = 0;

  private _placeholder!: string;
  protected _value?: string | void | null;
  private input$ = new Subject<string>();
  private selectedOption$ = new Subject<AutocompleteResult | null>();
  private destroy$ = new Subject<void>();
  public stateChanges = new Subject<void>();
  public focused = false;
  public predictions$?: Observable<AutocompleteResult[] | unknown>;
  public pendingFetch = false;
  private _disabled = false;
  public controlType = 'ui-address-autocomplete';
  private _required = false;
  private _errorState = false;
  public autofilled = false;

  @Input() classList = '';

  addressForm = this.fb.group({
    address: '',
  });

  @HostBinding('attr.aria-invalid')
  @Input()
  set errorState(state: boolean) {
    this._errorState = state;
    this.stateChanges.next(undefined);
  }
  get errorState() {
    return this._errorState;
  }

  @HostBinding('attr.aria-describedby') describedBy = '';
  @HostBinding()
  public id = `ui-address-autocomplete-${AddressAutocompleteComponent.nextId++}`;

  @Input() public hideUnderline?: boolean;

  @Output() public controlBlur = new EventEmitter();

  @ViewChild('addressInput', { static: true }) public addressInput?: ElementRef;
  @ViewChild('addressInput', { read: MatInput, static: true })
  public field!: MatInput;

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next(undefined);
  }

  @Input()
  get value(): string | void | null {
    return this._value;
  }

  set value(newValue: string | void | null) {
    if (newValue !== this._value) {
      this._value = newValue;
      this.addressForm?.get('address')?.setValue(newValue);
      this.cd.detectChanges();
      this.notifyChange(newValue);
      this.stateChanges.next(undefined);
    }
  }

  public notifyChange = (value: string | void | null) => undefined;
  public notifyTouch = () => undefined;

  public ngOnInit() {
    this.predictions$ = this.addressForm.valueChanges.pipe(
      map((formValue) => formValue.address),
      map((address) => (typeof address === 'string' ? { address } : address)),
      // we only look up autocomplete options for user inputted addresses
      filter((address) => this.isUserInputtedAddress(address)),
      tap((address: UserInputtedAddress) => (this.value = address.label || address.address)),
      // the delay should be AFTER the value is set
      debounceTime(50),
      switchMap((addressObject: UserInputtedAddress) =>
        addressObject.address && addressObject.address.length > 0
          ? this.gmaps.autocomplete({
              input: addressObject.address,
              offset: addressObject.address.length,
              // todo: make these configurable from outside
              types: ['address'],
              componentRestrictions: { country: 'us' },
            })
          : of([] as AutocompletePrediction[])
      ),
      map((results) => results.slice(0, 4)),
      map((results: AutocompletePrediction[]) =>
        results.map<AutocompleteResult>((item: AutocompletePrediction) => ({
          ...item,
          label: item.description.replace(/, USA$/, ''),
          value: item.structured_formatting.main_text,
        }))
      ),
      publishReplay(),
      refCount()
    );

    this.selectedOption$
      .pipe(
        takeUntil(this.destroy$),
        tap(() => (this.pendingFetch = true)),
        tap(() => this.addressForm.controls.address.disable()),
        switchMap((option) => (option ? this.gmaps.getPlace(option.place_id) : of(null))),
        tap(() => (this.pendingFetch = false)),
        tap(() => this.addressForm.controls.address.enable()),
        distinctUntilChanged()
      )
      .subscribe((value) => {
        this.value = value?.formatted_address?.replace(/, USA$/, '');
      });

    this.autofillMonitor.monitor(this.addressInput?.nativeElement).subscribe((e) => {
      this.autofilled = e.isAutofilled;
    });
  }

  get empty() {
    return !this._value;
  }

  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty || this.autofilled;
  }

  @Input()
  get required() {
    return this._required;
  }
  set required(req) {
    this._required = coerceBooleanProperty(req);
    this.stateChanges.next(undefined);
  }

  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis);
    this._disabled
      ? this.addressForm.controls['address'].disable()
      : this.addressForm.controls['address'].enable();
    this.stateChanges.next(undefined);
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }

  public ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  public inputChanged(input: string) {
    this.input$.next(input);
    this.selectedOption$.next(null);
  }

  public trackPredictions(index: number, prediction: AutocompleteResult) {
    return prediction.place_id;
  }

  public getPredictionLabel(prediction: AutocompleteResult): any {
    return prediction && prediction.label ? prediction.label : prediction;
  }

  public optionSelected(event: MatAutocompleteSelectedEvent) {
    this.selectedOption$.next(event.option.value as AutocompleteResult);
  }

  public writeValue(value: string): void {
    this.value = value;
  }

  public registerOnChange(cb: (value: string | void | null) => any): void {
    this.notifyChange = cb;
  }

  public registerOnTouched(cb: any): void {
    this.notifyTouch = cb;
  }

  public onBlur() {
    this.notifyTouch();
    this.controlBlur.emit();
  }

  private isUserInputtedAddress(address: AddressObject): address is UserInputtedAddress {
    return address && Object.prototype.hasOwnProperty.call(address, 'address'); // lint doesn't allow to use address.hasOwnProperty('address');
  }

  constructor(
    private fb: UntypedFormBuilder,
    private gmaps: GoogleMaps,
    @Optional()
    @Self()
    public ngControl: NgControl,
    private fm: FocusMonitor,
    private elRef: ElementRef,
    private cd: ChangeDetectorRef,
    private autofillMonitor: AutofillMonitor
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
    fm.monitor(elRef.nativeElement, true).subscribe((origin) => {
      this.focused = !!origin;
      this.stateChanges.next(undefined);
    });
  }
}
